import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useCallback } from 'preact/hooks';
import { hasTextContent, resolveDisplayHtml, resolveDisplayText } from '../utils';
import { useIsMouMobile } from '../utils/responsive';
import { useIsInView } from '../utils/useIsInView';
import { TCButton } from './TCButton';
import { PerksWallet } from './PerksWallet';
import { USPCheckbox } from './USPCheckbox';
/**
 * Individual offer card component for Multi-Offer Unit (MOU)
 * Uses internal ref and forwards DOM element to parent ref callback
 */
export function OfferCard({ offer, settings, styles, apiPayload, onOfferClick, onCheckboxChange, isSelected, showImage = true, index, totalOffers, hasAnyPerksWallet, hasAnyTnC, }) {
    const isMobile = useIsMouMobile();
    const useEqualCardHeight = !isMobile;
    const [isHovered, setIsHovered] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isInView, setViewRef] = useIsInView({
        isEmbedded: settings.embedded?.enabled === true,
        resetKey: offer.id,
    });
    const isUSPEnabled = settings.enable_user_selected_perks &&
        settings.enable_usp &&
        apiPayload?.pub_user_id;
    const shimmerEnabled = settings.enable_effect_shimmer_pos_cta === true;
    const yesBtnBackground = styles.offerText?.buttonYes?.background || '#007bff';
    const handleCardClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isUSPEnabled) {
            // If USP is enabled, toggle checkbox instead of opening offer
            const currentSelection = isSelected || false;
            onCheckboxChange?.(offer.campaign_id ?? offer.id, !currentSelection);
            return;
        }
        // Normal offer click behavior
        onOfferClick(offer);
        // Fire ad taken event
        if (window.Adpx?.adpxEvents?.adTaken) {
            window.Adpx.adpxEvents.adTaken(offer);
        }
    }, [offer, isUSPEnabled, isSelected, onOfferClick, onCheckboxChange]);
    const handleCTAClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        onOfferClick(offer);
        // Fire ad taken event
        if (window.Adpx?.adpxEvents?.adTaken) {
            window.Adpx.adpxEvents.adTaken(offer);
        }
    }, [offer, onOfferClick]);
    const handleImageError = () => {
        setImageError(true);
    };
    const getAdvertiserName = () => {
        const advName = offer.advertiser_name || offer.campaign?.advertiser_name || '';
        // Truncate long advertiser names on desktop
        if (advName.length > 28 && !isMobile) {
            return advName.substring(0, 28) + '...';
        }
        return advName;
    };
    const macroAttrs = {
        ...(apiPayload || {}),
        title: offer.title,
        advertiser_name: offer.advertiser_name,
        short_headline: offer.short_headline,
        short_description: offer.short_description,
    };
    const displayOfferValues = {
        title: resolveDisplayText(offer.short_headline, macroAttrs),
        description: resolveDisplayHtml(offer.short_description, macroAttrs),
        cta: resolveDisplayText(offer.cta_yes, macroAttrs),
    };
    // Styling
    const tileStyles = styles.offerwall?.tile;
    const shadowColor = styles.popup?.shadow;
    // On desktop the tiles always grow to share the unit's full width evenly
    // (e.g. 3 tiles in 1000px → ~333px each) rather than being pinned to the
    // 290px `max-width` cap from MOU.css. Mobile keeps the capped single-column layout.
    // With 3+ tiles in embedded mode the edge tiles sit flush against the unit
    // bounds, so a popup shadow gets clipped — inset the first/last tile by 6px.
    const insetEdgeForShadow = Boolean(settings.embedded?.enabled) &&
        totalOffers >= 3 &&
        Boolean(styles.popup?.shadow);
    const cardContainerStyles = {
        // Container/Grid positioning styles (from OfferGrid)
        flex: useEqualCardHeight ? '1 1 0' : undefined,
        maxWidth: useEqualCardHeight ? 'none' : undefined,
        minWidth: useEqualCardHeight ? 0 : undefined,
        marginBottom: isMobile && index === totalOffers - 1 ? '1rem' : '0',
        marginLeft: insetEdgeForShadow && index === 0 ? '6px' : undefined,
        marginRight: insetEdgeForShadow && index === totalOffers - 1 ? '6px' : undefined,
        // Card appearance styles
        position: 'relative',
        border: `${tileStyles?.offerwall_mou_border_thickness}px solid ${tileStyles?.offerwall_mou_border_color || '#ccc'}`,
        borderRadius: `${styles.offerwall?.tile_radius}px`,
        background: isHovered && !isMobile
            ? tileStyles?.offerwall_mou_tile_background_hover_color || '#f8f9fa'
            : tileStyles?.mou_tile_background_color || '#fff',
        boxShadow: shadowColor
            ? `2px 2px 10px 0px ${shadowColor}, -1px -1px 8.15603px 0px rgba(135, 149, 159, 0.40) inset, 2px 2px 8.15603px 0px rgba(255, 255, 255, 0.90) inset`
            : '0 2px 4px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '0',
        fontFamily: styles.offerText?.font || 'Roboto, sans-serif',
    };
    const imageContainerStyles = {
        position: 'relative',
        flexShrink: 0,
        width: '100%',
        height: isMobile ? '' : '70px',
        display: showImage && offer.image && !imageError ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'start',
        marginBottom: '8px',
    };
    const imageStyles = {
        width: '70px',
        height: '70px',
        maxWidth: '100%',
        objectFit: 'contain',
        borderRadius: '4px',
    };
    const contentStyles = {
        flex: useEqualCardHeight ? 1 : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minHeight: useEqualCardHeight ? '0' : undefined,
        width: '100%',
    };
    const advertiserNameStyles = {
        fontSize: `${styles.offerText?.fontSize || 12}px`,
        color: styles.offerText?.textColor || '#666',
        fontWeight: 'normal',
        margin: '0 0 4px 0',
        display: styles.offerText?.hide_adv_name ? 'none' : 'block',
        width: '100%',
        textAlign: 'left',
    };
    const titleStyles = {
        fontSize: `${styles.header?.heading_font_size || '14px'}`,
        color: styles.offerText?.textColor || '#000',
        fontWeight: 'bold',
        margin: '0 0 8px 0',
        lineHeight: '1.2',
        width: '100%',
        textAlign: 'left',
    };
    const descriptionStyles = {
        fontSize: `${styles.offerText?.fontSize || 12}px`,
        color: styles.offerText?.textColor || '#666',
        lineHeight: '1.4',
        margin: '0 0 12px 0',
        flex: useEqualCardHeight ? '1' : undefined,
        width: '100%',
        textAlign: 'left',
    };
    // The line-clamp uses `-webkit-box`, which mangles list rendering. Apply it only
    // when the description has no <ul>/<ol> so list content renders as a normal block.
    const descriptionClampStyles = {
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    };
    const ctaContainerStyles = {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        alignItems: 'center',
        flexWrap: 'wrap',
        width: '100%',
        marginTop: useEqualCardHeight ? 'auto' : undefined,
        minHeight: useEqualCardHeight && hasAnyPerksWallet && !isUSPEnabled ? '56px' : 'auto',
    };
    const ctaButtonStyles = {
        backgroundColor: styles.offerText?.buttonYes?.background || '#007bff',
        color: styles.offerText?.buttonYes?.color || '#fff',
        border: `2px solid ${styles.offerText?.buttonYes?.stroke || styles.offerText?.buttonYes?.background || '#007bff'}`,
        borderRadius: `${styles.offerText?.offerwall_mou_button_radius || 4}px`,
        fontSize: styles.offerText?.cta_text_size || '13px',
        fontWeight: styles.offerText?.cta_text_style || '400',
        fontFamily: styles.offerText?.font || 'Roboto, sans-serif',
        padding: '10px 12px',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        display: isUSPEnabled && !settings.embedded?.enabled ? 'none' : 'block',
        width: '100%',
        textAlign: 'center',
        // marginBottom: '4px', // Consistent spacing below button
    };
    const additionalCTAStyles = {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: 'auto',
    };
    return (_jsxs("div", { ref: setViewRef, style: cardContainerStyles, id: 'adpx_offer-container', className: 'adpx-mou_modal-content-container', onClick: handleCardClick, onMouseEnter: () => !isMobile && setIsHovered(true), onMouseLeave: () => !isMobile && setIsHovered(false), children: [offer.pixel && isInView && (_jsx("img", { style: { display: 'none' }, src: offer.pixel, alt: '', onLoad: () => { } })), isUSPEnabled && onCheckboxChange && (_jsx(USPCheckbox, { offer: offer, settings: settings, apiPayload: apiPayload, onSelectionChange: onCheckboxChange, isSelected: isSelected || false })), _jsx("div", { style: imageContainerStyles, children: _jsx("img", { src: offer.image, alt: offer.advertiser_name || 'Offer', style: imageStyles, onError: handleImageError }) }), _jsxs("div", { style: contentStyles, children: [getAdvertiserName() && (_jsx("div", { style: advertiserNameStyles, children: getAdvertiserName() })), _jsx("h3", { style: titleStyles, children: displayOfferValues.title }), hasTextContent(displayOfferValues.description) && (_jsx("div", { id: 'adpx_offer-description', className: 'adpx_offer-description', style: {
                            ...descriptionStyles,
                            ...(/<(ul|ol)[\s>]/i.test(displayOfferValues.description)
                                ? {}
                                : descriptionClampStyles),
                        }, dangerouslySetInnerHTML: { __html: displayOfferValues.description } })), _jsxs("div", { style: ctaContainerStyles, children: [_jsx("button", { style: ctaButtonStyles, className: shimmerEnabled
                                    ? 'adpx_btn-shimmer adpx_btn-shimmer-hover adpx_show-more-button adpx_btn-shimmer-anim'
                                    : undefined, onClick: handleCTAClick, onMouseEnter: e => {
                                    const el = e.currentTarget;
                                    if (!isMobile && styles.offerText?.buttonYes?.hover) {
                                        el.style.backgroundColor = styles.offerText.buttonYes.hover;
                                    }
                                    if (shimmerEnabled && window.innerWidth > 480) {
                                        el.style.boxShadow = `2px 0px 25px -1px ${yesBtnBackground}`;
                                    }
                                }, onMouseLeave: e => {
                                    const el = e.currentTarget;
                                    el.style.backgroundColor = yesBtnBackground;
                                    el.style.boxShadow = '';
                                }, children: displayOfferValues.cta || 'Yes Please' }), _jsxs("div", { style: additionalCTAStyles, children: [_jsx(PerksWallet, { offer: offer, settings: settings, styles: styles, userAttributes: apiPayload, additionalStyles: {
                                            marginTop: '0px',
                                            padding: '0',
                                        } }), _jsx(TCButton, { offer: offer, styles: styles, reserveSpace: useEqualCardHeight && hasAnyTnC })] })] })] })] }));
}
